import { Application } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.dev.auth.isaacmccourt.dev/v1';

/** Developer-console + playground API client. Uses the developer's portal session. */
class DevAPI {
  private base = API_BASE_URL;

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('admin_access_token');
    const res = await fetch(`${this.base}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.message || e.error || `HTTP ${res.status}`);
    }
    return res.json();
  }

  // Applications
  getApps = () => this.request<{ success: boolean; data: { applications: Application[] } }>('/console/apps').then((r) => r.data.applications);
  getApp = (appId: string) => this.request<any>(`/console/apps/${appId}`).then((r) => r.data);
  createApp = (name: string) => this.request<any>('/console/apps', { method: 'POST', body: JSON.stringify({ name }) }).then((r) => r.data.application);
  deleteApp = (appId: string) => this.request(`/console/apps/${appId}`, { method: 'DELETE' });
  getScopes = () => this.request<any>('/console/scopes').then((r) => r.data.scopes as string[]);

  // Keys
  issueKey = (appId: string, scopes: string[], name?: string) =>
    this.request<any>(`/console/apps/${appId}/keys`, { method: 'POST', body: JSON.stringify({ scopes, name }) }).then((r) => r.data);
  rotateKey = (appId: string, keyId: string) =>
    this.request<any>(`/console/apps/${appId}/keys/${keyId}/rotate`, { method: 'POST' }).then((r) => r.data);
  revokeKey = (appId: string, keyId: string) => this.request(`/console/apps/${appId}/keys/${keyId}`, { method: 'DELETE' });

  // Usage
  getUsage = (appId: string) => this.request<any>(`/console/apps/${appId}/usage`).then((r) => r.data);

  // Webhooks (self-serve, session-scoped)
  getWebhooks = () => this.request<any>('/console/webhooks').then((r) => r.data.webhooks);
  createWebhook = (url: string, events: string[]) => this.request<any>('/console/webhooks', { method: 'POST', body: JSON.stringify({ url, events }) }).then((r) => r.data.webhook);
  deleteWebhook = (id: string) => this.request(`/console/webhooks/${id}`, { method: 'DELETE' });

  // Status (reuses admin health)
  getHealth = () => this.request<any>('/admin/health').then((r) => r.data).catch(() => null);

  // Playground: proxy a dev-API call through the portal session.
  playground = async (method: string, path: string, body?: any) => {
    const res = await fetch(`${this.base}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_access_token')}` },
      body: body && method !== 'GET' ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    let json: any;
    try { json = JSON.parse(text); } catch { json = text; }
    return { status: res.status, body: json };
  };
}

export const devAPI = new DevAPI();
